import { useLocale } from '@koopajs/react';
import { User } from '@koopajs/react';
import Timeline from '@mui/lab/Timeline';
import TimelineItem, { timelineItemClasses } from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import { DateTimeStartEnd } from 'components/DateTimeStartEnd';
import { SystemStyleObject } from '@mui/system';
import { Box, Typography, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useState } from 'react';

interface IEventsTimeline {
  sx?: SystemStyleObject;
  timelineEvents: {
    title: string;
    timestamp?: string;
    userId?: string;
  }[];
}

export const EventsTimeline: React.FC<IEventsTimeline> = (props) => {
  const { timelineEvents, sx } = props;
  const [expanded, setExpanded] = useState<boolean>(false);

  const { t } = useLocale();
  const keyPrefix = 'Components.EventsTimeline';

  const handleAccordionChange = () => {
    setExpanded(!expanded);
  };

  const timelineEventsSorted = timelineEvents
    .filter((event) => event.timestamp)
    .sort((a, b) => {
      const dateA = a.timestamp ? new Date(a.timestamp).valueOf() : Infinity;
      const dateB = b.timestamp ? new Date(b.timestamp).valueOf() : Infinity;
      return dateB - dateA;
    });

  return (
    <Box sx={{ ...sx, maxWidth: '700px' }}>
      <Accordion
        expanded={expanded}
        onChange={handleAccordionChange}
        sx={{
          boxShadow: 'none',
          '&:before': {
            display: 'none'
          },
          backgroundColor: 'transparent'
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            padding: 0,
            minHeight: 'auto',
            justifyContent: 'flex-start',
            '& .MuiAccordionSummary-content': {
              margin: 0,
              width: 'auto', // Don't expand to fill width
              flexGrow: 0, // Don't grow to fill space
              justifyContent: 'flex-start'
            },
            '& .MuiAccordionSummary-expandIconWrapper': {
              marginLeft: '8px', // Margin between title and icon
              position: 'relative'
            }
          }}
        >
          <Typography sx={{ fontSize: '16px' }} variant="h6">
            {t(keyPrefix + '.title')}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ padding: 0, pt: 1 }}>
          <Timeline
            sx={{
              p: 0,
              m: 0,
              [`& .${timelineItemClasses.root}:before`]: {
                flex: 0,
                padding: 0
              },
              '& .MuiTimelineItem-root': {
                minHeight: 'auto',
                mb: 1.5,
                py: 0,
                position: 'relative'
              },
              '& .MuiTimelineDot-root': {
                margin: 0,
                padding: 0,
                width: '8px',
                height: '8px',
                backgroundColor: 'transparent',
                borderColor: 'rgba(0, 0, 0, 0.3)',
                boxShadow: 'none',
                marginTop: '6px',
                zIndex: 1
              },
              '& .MuiTimelineContent-root': {
                padding: '0 8px',
                pt: 0,
                pb: 0
              },
              '& .MuiTimelineSeparator-root': {
                minWidth: 'auto',
                pr: 0
              }
            }}
          >
            <>
              {timelineEventsSorted.map((event, index, array) => {
                const isLast = index === array.length - 1;

                const { title, timestamp, userId } = event;

                return (
                  <TimelineItem key={JSON.stringify(event)}>
                    <TimelineSeparator>
                      <TimelineDot variant="outlined" />
                      {!isLast && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: '14px',
                            bottom: '-18px',
                            left: '3px',
                            width: '2px',
                            backgroundColor: 'rgba(0, 0, 0, 0.12)'
                          }}
                        />
                      )}
                    </TimelineSeparator>
                    <TimelineContent>
                      <Typography variant="body2">
                        {title}{' '}
                        <DateTimeStartEnd
                          isoStringDate={timestamp}
                          dateFormat="DATE_FULL"
                          isoStringStart={timestamp}
                        />{' '}
                        {userId && (
                          <>
                            {t('common:by')} <User id={userId}>{(profile) => <>{profile?.username}</>}</User>
                          </>
                        )}
                      </Typography>
                    </TimelineContent>
                  </TimelineItem>
                );
              })}
            </>
          </Timeline>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};
